// engine/scene/SceneManager.ts
import { Scene } from './Scene';
import { logger } from '../logging';

export interface SceneModeConfig<TMode = string, TScene = Scene> {
  mode: TMode;
  sceneFactory: () => TScene | Promise<TScene>;
  sceneType: (new (...args: any[]) => TScene) | null;
}

export interface SceneModeManagerConfig<TMode = string> {
  modeStore: {
    get(): TMode;
    subscribe(callback: (mode: TMode) => void): () => void;
  };
  scenes: SceneModeConfig<TMode>[];
  defaultMode: TMode;
}

export class SceneManagerInstance {
  private sceneStack: Scene[] = [];
  private modeUnsubscribe: (() => void) | null = null;
  private modeConfig: SceneModeManagerConfig<any> | null = null;
  private managers: any = null;

  get current() {
    return this.sceneStack.at(-1) ?? null;
  }

  /**
   * Установить менеджеры для передачи в сцены
   */
  setManagers(managers: any): void {
    this.managers = managers;
  }

  switch(nextScene: Scene) {
    const previousScene = this.current;
    if (previousScene) previousScene.onExit(nextScene);
    this.sceneStack = [nextScene];
    nextScene.onEnter(previousScene);
  }

  /**
   * Горячая замена сцены для HMR
   * Корректно отписывает старую сцену и инициализирует новую
   */
  replace(newScene: Scene, managers?: any) {
    const previousScene = this.current;

    if (previousScene) {
      // 1. Отписываем старую сцену
      previousScene.onExit(newScene);

      // 2. Очищаем все GameObject'ы из старой сцены
      for (const gameObject of previousScene.gameObjects) {
        gameObject._onRemovedFromScene();
      }
      previousScene.gameObjects.length = 0;
    }

    // 3. Инициализируем новую сцену с менеджерами
    const managersToUse = managers || this.managers;
    if (managersToUse) {
      newScene.initialize(managersToUse);
    }

    // 4. Заменяем сцену
    this.sceneStack = [newScene];
    newScene.onEnter(previousScene);
  }

  push(nextScene: Scene) {
    const previousScene = this.current;
    if (previousScene) previousScene.onSuspend();
    this.sceneStack.push(nextScene);
    nextScene.onEnter(previousScene);
  }

  pop() {
    const topScene = this.sceneStack.pop();
    const currentScene = this.current;
    if (topScene) topScene.onExit(currentScene);
    if (currentScene) currentScene.onResume();
  }

  update(deltaTime: number) {
    this.current?.update(deltaTime);
  }

  /**
   * Инициализация режимного переключения сцен
   */
  initializeModeSwitching<TMode = string>(config: SceneModeManagerConfig<TMode>): void {
    if (this.modeConfig) {
      logger.warn('⚠️ Scene mode switching already initialized', { source: 'SceneManager' });
      return;
    }

    this.modeConfig = config;
    logger.info('✅ Scene mode switching initialized', { source: 'SceneManager' });
  }

  /**
   * Включение режимного переключения сцен
   */
  enableModeSwitching(): void {
    if (!this.modeConfig) {
      throw new Error('Scene mode switching must be initialized before enabling');
    }

    if (this.modeUnsubscribe) {
      logger.warn('⚠️ Scene mode switching already enabled', { source: 'SceneManager' });
      return;
    }

    // Подписываемся на изменения режима
    this.modeUnsubscribe = this.modeConfig.modeStore.subscribe(mode => {
      void this.handleModeChange(mode);
    });

    logger.info('✅ Scene mode switching enabled', { source: 'SceneManager' });
  }

  /**
   * Отключение режимного переключения сцен
   */
  disableModeSwitching(): void {
    if (this.modeUnsubscribe) {
      this.modeUnsubscribe();
      this.modeUnsubscribe = null;
      logger.info('✅ Scene mode switching disabled', { source: 'SceneManager' });
    }
  }

  /**
   * Обработка изменения режима
   */
  private async handleModeChange(mode: any): Promise<void> {
    if (!this.modeConfig) {
      logger.warn('⚠️ Scene mode config not available for mode change', { source: 'SceneManager' });
      return;
    }

    const currentScene = this.current;

    // Проверяем, нужно ли переключать сцену
    if (this.shouldSkipSceneSwitch(mode, currentScene)) {
      logger.debug(`🎯 Mode '${String(mode)}' already active, skipping scene switch`, {
        source: 'SceneManager',
      });
      return;
    }

    logger.info(`🔄 Switching scene to: ${String(mode)}`, { source: 'SceneManager' });

    // Создаем новую сцену в зависимости от режима
    const newScene = await this.createSceneForMode(mode);

    if (newScene) {
      // Используем replace для горячей замены сцены
      this.replace(newScene);
    }
  }

  /**
   * Проверка, нужно ли пропустить переключение сцены
   */
  private shouldSkipSceneSwitch(mode: any, currentScene: Scene | null): boolean {
    if (!this.modeConfig || !currentScene) return false;

    const sceneConfig = this.modeConfig.scenes.find(scene => scene.mode === mode);
    if (!sceneConfig || !sceneConfig.sceneType) return false;

    return currentScene instanceof sceneConfig.sceneType;
  }

  /**
   * Создание сцены для указанного режима
   */
  private async createSceneForMode(mode: any): Promise<Scene | null> {
    if (!this.modeConfig) {
      logger.warn('⚠️ Scene mode config not available', { source: 'SceneManager' });
      return null;
    }

    const sceneConfig = this.modeConfig.scenes.find(scene => scene.mode === mode);

    if (sceneConfig) {
      return await sceneConfig.sceneFactory();
    }

    // Fallback на defaultMode
    const defaultSceneConfig = this.modeConfig.scenes.find(
      scene => scene.mode === this.modeConfig!.defaultMode
    );
    if (defaultSceneConfig) {
      logger.warn(
        `⚠️ Unknown mode: ${String(mode)}, falling back to default mode: ${String(this.modeConfig.defaultMode)}`,
        { source: 'SceneManager' }
      );
      return await defaultSceneConfig.sceneFactory();
    }

    logger.error(
      `❌ No scene configuration found for mode: ${String(mode)} and no default mode available`,
      new Error('Scene configuration not found'),
      { source: 'SceneManager' }
    );
    return null;
  }

  /**
   * Проверка статуса режимного переключения
   */
  get isModeSwitchingEnabled(): boolean {
    return this.modeUnsubscribe !== null;
  }

  /**
   * Создание стартовой сцены на основе defaultMode
   */
  async createStartScene(): Promise<Scene | null> {
    if (!this.modeConfig) {
      logger.warn('⚠️ Scene mode config not available for start scene', { source: 'SceneManager' });
      return null;
    }

    const defaultSceneConfig = this.modeConfig.scenes.find(
      scene => scene.mode === this.modeConfig!.defaultMode
    );
    if (!defaultSceneConfig) {
      logger.error(
        `❌ No scene configuration found for default mode: ${String(this.modeConfig.defaultMode)}`,
        new Error('Default scene configuration not found'),
        { source: 'SceneManager' }
      );
      return null;
    }

    logger.info(`🎬 Creating start scene for mode: ${String(this.modeConfig.defaultMode)}`, {
      source: 'SceneManager',
    });
    return await defaultSceneConfig.sceneFactory();
  }

  /**
   * Очистка режимного переключения
   */
  cleanup(): void {
    this.disableModeSwitching();
    this.modeConfig = null;
  }
}

let sceneManagerInstance: SceneManagerInstance | null = null;
export const SceneManager = () => (sceneManagerInstance ??= new SceneManagerInstance());
